/**
 * Unified Domain Tab Component
 * 
 * Replaces: LocationTab, VendorTab, InsuranceTab, OEMTab
 * 
 * These tabs were nearly identical - only differences were:
 * - domain type passed to hooks/components
 * - domainLabel for display
 * - whether locationId filter is used
 * 
 * This single component handles all four domain types.
 */
import { useState, useMemo, useCallback, useContext } from 'react';
import { Box } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import SessionContext from '../../../contexts/SessionContext';
import { getDomainStatsQueryConfig } from '../../../hooks/reporting/useDomainStats';
import { useReportingDomainStats } from '../../../hooks/reporting/useReportingDomainStats';
import { DomainRankingSection, DomainTimeseriesChart } from '../components';
import { useReportingFilters } from '../ReportingFiltersContext';
import { loadViewPrefs } from '../../../hooks/reporting/useReportingViewPrefs';
import { VendorGroupBy } from '../../../types/reporting';
import { getReportingComparison } from '../shared/reportingComparisonUtils';

export type DomainType = 'location' | 'vendor' | 'insurance' | 'oem';

interface DomainTabProps {
    domain: DomainType;
    startDate: string;
    endDate: string;
    locationId?: string;
    granularity: 'day' | 'week' | 'biweekly' | 'month';
    preset?: string;
    /** When false, keep React Query cache but do not fetch. */
    isActive?: boolean;
}

// Labels for display
const DOMAIN_LABELS: Record<DomainType, string> = {
    location: 'Location',
    vendor: 'Vendor',
    insurance: 'Insurance Carrier',
    oem: 'OEM',
};

const DomainTab = ({ 
    domain, 
    startDate, 
    endDate, 
    locationId, 
    granularity,
    preset,
    isActive = true,
}: DomainTabProps) => {
    const { excludeEvs } = useReportingFilters();
    const queryClient = useQueryClient();
    const sessionContext = useContext(SessionContext);
    const [sortBy, setSortBy] = useState('orderVolume');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    const vendorViewPrefsKey = domain === 'vendor' ? 'reporting_domain_vendor_view' : '';
    const initialVendorGroupBy = useMemo(() => {
        if (domain !== 'vendor') return 'vendor' as VendorGroupBy;
        const saved = loadViewPrefs<Partial<{ vendorGroupBy: VendorGroupBy }>>(vendorViewPrefsKey, {});
        return saved.vendorGroupBy ?? 'vendor';
    }, [domain, vendorViewPrefsKey]);
    const [vendorGroupBy, setVendorGroupBy] = useState<VendorGroupBy>(initialVendorGroupBy);
    
    const comparison = getReportingComparison(preset);
    
    // For location tab, don't filter by location (it IS the location breakdown)
    const filterLocationId = domain === 'location' ? undefined : locationId;

    const domainStatsBaseOptions = useMemo(() => ({
        locationId: filterLocationId,
        sortBy,
        sortOrder,
        preset,
        comparison,
        excludeEvs,
    }), [filterLocationId, sortBy, sortOrder, preset, comparison, excludeEvs]);

    const prefetchVendorGroupBy = useCallback((groupBy: VendorGroupBy) => {
        if (!isActive || domain !== 'vendor') return;
        const sessionId = sessionContext?.sessionId;
        if (!sessionId || !startDate || !endDate) return;

        void queryClient.prefetchQuery(
            getDomainStatsQueryConfig(sessionId, domain, startDate, endDate, {
                ...domainStatsBaseOptions,
                groupBy,
            }),
        );
    }, [isActive, domain, sessionContext?.sessionId, startDate, endDate, domainStatsBaseOptions, queryClient]);
    
    // Fetch domain stats for ranking section and entity list (bundle-first on rollup)
    const { data: statsData, isLoading: statsLoading, isFetching: statsFetching } = useReportingDomainStats(
        domain,
        startDate,
        endDate,
        {
            ...domainStatsBaseOptions,
            granularity,
            groupBy: domain === 'vendor' ? vendorGroupBy : undefined,
            enabled: isActive,
        },
    );

    const handleSortChange = (field: string) => {
        if (field === sortBy) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
    };

    const domainLabel = DOMAIN_LABELS[domain];

    return (
        <Box>
            {/* Graph/Table - Ranking Section (Full Width) */}
            <Box mb={6}>
                <DomainRankingSection
                    domain={domain}
                    items={statsData?.items || []}
                    totals={statsData?.totals || {} as any}
                    isLoading={statsLoading}
                    isFetching={statsFetching}
                    domainLabel={domainLabel}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSortChange={handleSortChange}
                    defaultMetric="orderVolume"
                    maxChartItems={12}
                    comparison={comparison}
                    preset={preset}
                    tableExperienceKey={`reporting_domain_${domain}`}
                    vendorGroupBy={domain === 'vendor' ? vendorGroupBy : undefined}
                    onVendorGroupByChange={domain === 'vendor' ? setVendorGroupBy : undefined}
                    onPrefetchVendorGroupBy={domain === 'vendor' ? prefetchVendorGroupBy : undefined}
                />
            </Box>

            {/* Graph Over Time Section - same height, WidgetCard-style header */}
            <Box
                bg="white"
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.200"
                shadow="none"
                display="flex"
                flexDirection="column"
                h="500px"
                overflow="hidden"
            >
                <DomainTimeseriesChart
                    domain={domain}
                    startDate={startDate}
                    endDate={endDate}
                    entities={statsData?.items || []}
                    granularity={granularity}
                    locationId={filterLocationId}
                    defaultMetric="orderVolume"
                    preset={preset}
                    comparison={comparison}
                    vendorGroupBy={domain === 'vendor' ? vendorGroupBy : undefined}
                    onPrefetchVendorGroupBy={domain === 'vendor' ? prefetchVendorGroupBy : undefined}
                    enabled={isActive}
                />
            </Box>
        </Box>
    );
};

export default DomainTab;

// ============================================================================
// Legacy Wrapper Components (for backwards compatibility)
// These can be removed once ReportingPage.tsx is updated to use DomainTab directly
// ============================================================================

export const LocationTab = (props: Omit<DomainTabProps, 'domain'>) => (
    <DomainTab domain="location" {...props} />
);

export const VendorTab = (props: Omit<DomainTabProps, 'domain'>) => (
    <DomainTab domain="vendor" {...props} />
);

export const InsuranceTab = (props: Omit<DomainTabProps, 'domain'>) => (
    <DomainTab domain="insurance" {...props} />
);

export const OEMTab = (props: Omit<DomainTabProps, 'domain'>) => (
    <DomainTab domain="oem" {...props} />
);
